import React from "react"
import Layout from "../../components/layout"
import SubNavigation from "../../components/sub-navigation"
import Module from "../../components/module"
import ReadNext from "../../components/read-next"
import WhaleVideo from "../../video/climate-change/whale.mp4"
import GlobalTemperaturesIncrease from "../../images/spotlights/climate-change/climate-change-chart.inline.svg"
import { AppendixWaypoint, Appendix } from "../../components/appendix"

const PAGESLUG = "climate-change"

export default function ClimateChange() {
  return (
    <Layout>
      <SubNavigation id="A Crisis Like No Other" current={PAGESLUG} />
      <section className="page__section page__section--sidebars">
        <div className="page__section__sidebar page__section__sidebar--left"></div>
        <div className="page__section__inner">
          <div className="container container--medium">
            <h1 className="page__title--opener">
              Climate change is a linchpin of the Sustainable Development Goals
            </h1>
            <h2 className="page__title">
              The IMF is helping countries implement mitigation strategies,
              build resilience, and manage risk.
            </h2>
            <p>
              Climate change remains a clear and present threat, yet actions to
              fight it have fallen short. Measures taken now to fight the
              climate crisis will create more resilient economies and help the
              recovery from the global pandemic be fair, smart, and green.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={3} />
            <p>
              The IMF has contributed to global efforts by delivering analysis
              and enhancing country engagement on climate change. It will step
              up its efforts as the institution looks ahead to the United
              Nations Climate Change Conference in November 2021.
            </p>
            <p>
              The IMF’s work with finance ministries, central banks, and
              financial regulators uniquely positions it to help counsel on
              these policies.
            </p>
            <Module
              title="Curbing Climate Change"
              subtitle="Carbon emissions are a chief contributor to global warming. One viable strategy to limit global warming is for countries to introduce a carbon tax to discourage the production of emissions and help curb climate change."
              label="Source: October 2019 <em>Fiscal Monitor</em>."
              color="green"
              classes="util__mobile-only"
              content={
                <figure
                  aria-label="Chart of carbon emissions taxation policies and the projected result in global temperature increase by 2100. No mitigation and unchanged carbon emissions taxation yield a projected temperature increase of 4 degrees Celsius. Increased carbon emissions taxation of $25 to $50 per ton yields a projected temperature increase of 2.5 degrees Celsius. High carbon emissions taxation of $75 per ton yields a projected temperature increase of 2 degrees Celsius."
                >
                  <GlobalTemperaturesIncrease />
                </figure>
              }
            />
            <p>
              On climate change mitigation, the IMF provided practical,
              country-specific guidance on fiscal and other policy options to
              implement mitigation strategies, such as carbon pricing and fossil
              fuel subsidy reform.
            </p>
            <p>
              To help countries cope with natural disasters when they happen,
              the IMF has deepened the financial safety net by increasing access
              limits for emergency financing.
            </p>
            <p>
              To help countries adapt and build resilience to climate change,
              the IMF, together with the World Bank, provided overarching
              assessments of preparedness, macroeconomic impact, mitigation,
              adaptation, and financing strategies for small, vulnerable, and
              capacity-constrained countries.
            </p>
            <p>
              As part of this work, the IMF has published Climate Change Policy
              Assessments for Grenada, Micronesia, and Tonga this year.
            </p>
            <AppendixWaypoint page={PAGESLUG} id={4} />
            <p>
              The transition to a low-carbon economy will mean countries have to
              manage a number of risks. The IMF has proposed a carbon price
              floor arrangement among countries to effectively and equitably
              scale up global mitigation action. Other policy work has focused
              on financial regulation to internalize risks from exposure to
              “brown” assets such as fossil fuels, as well as on measures to
              ease the social impact of carbon mitigation or the need to
              diversify in economies that depend on fossil fuel exports.
            </p>
          </div>
        </div>
        <div className="page__section__sidebar page__section__sidebar--right page__section__sidebar--justify-center">
          <Module
            title="Curbing Climate Change"
            subtitle="Carbon emissions are a chief contributor to global warming. One viable strategy to limit global warming is for countries to introduce a carbon tax to discourage the production of emissions and help curb climate change."
            label="Source: October 2019 <em>Fiscal Monitor</em>."
            content={
              <figure
                className="scaling-svg-container"
                style={{ paddingBottom: "97%" }}
                aria-label="Chart of carbon emissions taxation policies and the projected result in global temperature increase by 2100. No mitigation and unchanged carbon emissions taxation yield a projected temperature increase of 4 degrees Celsius. Increased carbon emissions taxation of $25 to $50 per ton yields a projected temperature increase of 2.5 degrees Celsius. High carbon emissions taxation of $75 per ton yields a projected temperature increase of 2 degrees Celsius."
              >
                <GlobalTemperaturesIncrease />
              </figure>
            }
          />
        </div>
      </section>
      <Appendix page={PAGESLUG} />
      <ReadNext sectionID="spotlights" pageID="fintech" />
      <div className="page__background-video" role="presentation">
        <div className="page__background-video__inner">
          <video
            src={WhaleVideo}
            muted
            loop
            playsInline
            autoPlay={true}
            role="presentation"
          ></video>
        </div>
      </div>
    </Layout>
  )
}
